import ms from 'milsymbol';
import { CanvasSource, Texture } from 'pixi.js';

/**
 * MIL-STD-2525 (the US military symbology standard) symbol textures for the Pixi overlay.
 *
 * milsymbol rasterises an SVG per call, which is far too slow to do per frame, so every
 * SIDC (symbol identification code) + size pair is rendered once into a canvas and kept as a
 * Pixi texture. Textures are rendered at the device pixel ratio and tagged with a matching
 * resolution so a sprite's logical size stays `size` CSS pixels while staying crisp on retina.
 */

export interface SymbolAsset {
  texture: Texture;
  /** Normalised anchor (0..1) of the symbol's octagon centre, i.e. the point on the ground. */
  anchor: { x: number; y: number };
  /** Rendered size in CSS pixels. */
  width: number;
  height: number;
}

const cache = new Map<string, SymbolAsset>();

function dpr(): number {
  const r = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
  return Math.min(Math.max(r, 1), 3);
}

/** True when milsymbol recognises the code; used by callers to fail loudly on a bad SIDC. */
export function isValidSidc(sidc: string): boolean {
  try {
    return new ms.Symbol(sidc, { size: 20 }).isValid() === true;
  } catch {
    return false;
  }
}

/**
 * Texture plus ground anchor for one SIDC at one on-screen size, cached. `size` is milsymbol's
 * icon size; the rendered bitmap is taller because of the frame, echelon marks and modifiers, so
 * the sprite must be anchored on the octagon centre rather than the bitmap centre.
 */
export function symbolAsset(sidc: string, size = 22): SymbolAsset {
  const key = `${sidc}@${size}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const ratio = dpr();
  const symbol = new ms.Symbol(sidc, {
    size,
    frame: true,
    fill: true,
    // `monoColor` must stay at milsymbol's default of '' — passing undefined puts the symbol on
    // the monochrome path with no colour, which draws the outline pass and nothing else.
    monoColor: '',
    // A dark outline keeps the symbol readable over bright hillshade.
    outlineWidth: 2,
    outlineColor: '#0b0f14',
  });
  const canvas = symbol.asCanvas(ratio);
  const source = new CanvasSource({ resource: canvas, resolution: ratio });
  const texture = new Texture({ source });
  const { width, height } = symbol.getSize();
  const oct = symbol.getOctagonAnchor();
  const asset: SymbolAsset = {
    texture,
    anchor: { x: width ? oct.x / width : 0.5, y: height ? oct.y / height : 0.5 },
    width,
    height,
  };
  cache.set(key, asset);
  return asset;
}

/** The cached texture alone (the interface the plan names). */
export function symbolTexture(sidc: string, size = 22): Texture {
  return symbolAsset(sidc, size).texture;
}

/** Drop every cached texture (used when the renderer is torn down in a hot reload). */
export function clearSymbolCache(): void {
  for (const a of cache.values()) a.texture.destroy(true);
  cache.clear();
}
